// "All sessions" — dense ledger view of every chat session, intentionally
// distinct from the sidebar's "Recent" list (which shows ~7 above the fold
// with the rest behind a scroll). The 6-card preview grid was dropped in the
// 2026-05-11 UI refresh because it duplicated the sidebar; we keep the
// LedgerTable view alone for at-a-glance scanning over the full archive.

import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";

import { LedgerTable } from "@/components/editorial/LedgerTable";
import { SectionHead } from "@/components/editorial/SectionHead";
import { listSessions } from "@/services/api/sessions";

export function History() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ["sessions", "history-all"],
    queryFn: () => listSessions(200),
  });

  const sessions = data ?? [];

  return (
    <div className="space-y-10">
      <SectionHead
        label="All sessions"
        italicSuffix="full archive"
        count={sessions.length || undefined}
      />

      {isLoading ? (
        <p className="text-warmgray-400">Loading…</p>
      ) : error ? (
        <p className="text-oxblood-500">Could not load sessions: {(error as Error).message}</p>
      ) : sessions.length === 0 ? (
        <p className="font-serif italic text-warmgray-400">
          No conversations yet. Start one in Chat.
        </p>
      ) : (
        <LedgerTable
          rows={sessions.map((s) => ({
            time: s.last_active_at
              ? format(parseISO(s.last_active_at), "MMM d · HH:mm")
              : "—",
            kind: `${s.turn_count} turn${s.turn_count === 1 ? "" : "s"}`,
            title: s.title ?? "Untitled session",
            status: s.created_at ? format(parseISO(s.created_at), "MMM d") : "—",
            onClick: () => navigate(`/history/${s.session_id}`),
          }))}
        />
      )}
    </div>
  );
}
